/**
 * CompanyCustomersPage — list of recurring senders who used the company.
 * Mirrors Flutter `company/company_customers_page.dart`.
 *
 * Tap a row → CompanyCustomerDetailPage (every shipment + container
 * participation that customer made via this company).
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Users, Loader, ChevronLeft, Phone } from 'lucide-react';
import { useMyCompany, useCompanyCustomers } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import type { CompanyCustomer } from '@/types/companyExtras';

export default function CompanyCustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: company, isLoading: companyLoading } = useMyCompany();
  const { data: customers, isLoading } = useCompanyCustomers();

  if (companyLoading || isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!company) {
    navigate('/company', { replace: true });
    return null;
  }

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
          <Users size={20} /> {t('customers_title', 'الزبائن')}
        </h1>
        <p className="text-text-secondary text-sm mb-5">
          {t('customers_subtitle', 'المرسلون الذين تعاملوا مع شركتك. اضغط على زبون لرؤية كل تعاملاته.')}
        </p>

        {!customers || customers.length === 0 ? (
          <div className="bg-surface border border-border rounded-2xl p-10 text-center">
            <Users size={48} className="mx-auto text-text-tertiary mb-3" />
            <p className="text-text-secondary">
              {t('customers_empty', 'لم يتعامل أحد مع شركتك بعد.')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {customers.map((c) => (
              <CustomerRow
                key={c.user_id}
                customer={c}
                onTap={() => navigate(`/company/customers/${encodeURIComponent(c.user_id)}`)}
              />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function CustomerRow({ customer, onTap }: { customer: CompanyCustomer; onTap: () => void }) {
  const { t } = useTranslation();
  const fullName = [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim();
  return (
    <button
      onClick={onTap}
      className="w-full text-right bg-surface border border-border rounded-xl p-4 hover:border-primary/40 transition-all"
    >
      <div className="flex items-center gap-3">
        {customer.avatar_url ? (
          <img
            src={customer.avatar_url}
            alt={fullName || customer.user_id}
            className="w-10 h-10 rounded-full object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div className="bg-primary/10 text-primary w-10 h-10 rounded-full flex items-center justify-center font-bold">
            {(fullName || '?').charAt(0)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-text-primary truncate">
            {fullName || t('anonymous', 'مستخدم')}
          </div>
          {customer.phone && (
            <div className="flex items-center gap-1 text-xs text-text-secondary">
              <Phone size={11} /> <span dir="ltr">{customer.phone}</span>
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-text-primary">
            {customer.total_count} {t('interactions', 'تعاملاً')}
          </div>
          <div className="text-xs text-text-secondary">
            {customer.shipments_count} {t('shipments', 'شحنة')} · {customer.containers_count} {t('participations', 'مشاركة')}
          </div>
        </div>
        <ChevronLeft size={18} className="text-text-tertiary" />
      </div>
    </button>
  );
}
